import { Activity, ListTree, UserCheck, HeartPulse, MousePointer2 } from "lucide-react";
import { useExecution } from "@/store/executionStore";
import { ExecutionOverview } from "./ExecutionOverview";
import { ExecutionList } from "./ExecutionList";
import { ExecutionTimeline } from "./ExecutionTimeline";
import { ExecutionDetailPanel } from "./ExecutionDetailPanel";
import { ApprovalQueue } from "./ApprovalQueue";
import { ApprovalModal } from "./ApprovalModal";
import { SystemHealthPanel } from "./SystemHealthPanel";
import { Segmented } from "@/components/hive/shared";

type View = "executions" | "approvals" | "health";

export function ExecutionCenterWindow() {
  const executions = useExecution((s) => s.executions);
  const selectedId = useExecution((s) => s.selectedExecutionId);
  const approvals = useExecution((s) => s.approvals);
  const health = useExecution((s) => s.systemHealth);
  const view = useExecution((s) => s.centerView);
  const setView = useExecution((s) => s.setCenterView);

  const exec = executions.find((e) => e.id === selectedId) ?? executions[0] ?? null;
  const pending = approvals.filter((a) => a.status === "pending").length;
  const unhealthy = health.filter((h) => h.status !== "healthy").length;

  return (
    <div className="flex h-full flex-col bg-[hsl(var(--bg-1))]">
      {/* header */}
      <div className="flex items-center gap-3 border-b border-app px-4 py-3">
        <div className="grid h-9 w-9 place-items-center rounded-xl text-white shadow-lg" style={{ background: "linear-gradient(135deg, #22d3ee, #0891b2)" }}>
          <HeartPulse size={18} />
        </div>
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-app">
            Execution Center
            <span className="flex items-center gap-1 text-[11px] font-normal text-cyan-400">
              <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-60" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-400" /></span>
              live
            </span>
          </div>
          <div className="text-[11px] text-app-2">The operational heartbeat of Hive</div>
        </div>
        <div className="ml-auto">
          <Segmented<View>
            value={view}
            onChange={setView}
            accent="#22d3ee"
            options={[
              { value: "executions", label: "Executions", icon: <ListTree size={12} /> },
              { value: "approvals", label: `Approvals${pending ? ` · ${pending}` : ""}`, icon: <UserCheck size={12} /> },
              { value: "health", label: `Health${unhealthy ? ` · ${unhealthy}` : ""}`, icon: <Activity size={12} /> },
            ]}
          />
        </div>
      </div>

      <ExecutionOverview />

      <div className="mt-3 min-h-0 flex-1 border-t border-app">
        {view === "executions" && (
          <div className="flex h-full">
            <ExecutionList />
            <div className="min-w-0 flex-1">
              {exec ? <ExecutionTimeline exec={exec} /> : <EmptyTimeline />}
            </div>
            {exec && <ExecutionDetailPanel exec={exec} />}
          </div>
        )}
        {view === "approvals" && <ApprovalQueue />}
        {view === "health" && <SystemHealthPanel />}
      </div>

      <ApprovalModal />
    </div>
  );
}

function EmptyTimeline() {
  return (
    <div className="grid h-full place-items-center text-center">
      <div>
        <MousePointer2 size={22} className="mx-auto text-app-2" />
        <div className="mt-2 text-xs text-app-2">Select an execution to see its timeline</div>
      </div>
    </div>
  );
}
